import { useEffect, useState } from 'react';
import { sprayService } from '@iso/services';
import { mergeRequiredSections } from '@iso/constants/sprayRequiredSections';

/**
 * Load the property's required-confirmations setting for the spray flow.
 *
 * Returns a stable shape: { sections, loading } where `sections` is always a
 * boolean map keyed by section id (defaults applied), even before the API
 * round-trip completes — so gate calculations can be performed immediately
 * with sensible fallbacks.
 *
 * @param {number|string|null} propertyId
 * @returns {{ sections: Object<string, boolean>, loading: boolean }}
 */
const useSprayRequiredSections = (propertyId) => {
  const [sections, setSections] = useState(mergeRequiredSections(null));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    setLoading(true);
    sprayService
      .getSettings(propertyId)
      .then((res) => {
        if (cancelled) return;
        const override = res?.settings?.required_confirmations?.setting_value;
        setSections(mergeRequiredSections(override));
      })
      .catch((err) => {
        console.error('useSprayRequiredSections: failed to load settings', err);
        // Keep defaults on failure — never silently weaken the gates.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  return { sections, loading };
};

export default useSprayRequiredSections;
